import React, { useState } from 'react';
import {
  TicketIcon,
  GiftIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

const PromoReferrals: React.FC = () => {
  const [promoCode, setPromoCode] = useState('');
  const [referralCode] = useState('VOLEXX-' + Math.random().toString(36).substring(7).toUpperCase());
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [appliedPromos, setAppliedPromos] = useState<any[]>([]);
  const [referrals, setReferrals] = useState({
    total: 0,
    successful: 0,
    pending: 0,
    earnings: 0
  });

  const copyReferralLink = () => {
    const referralLink = `https://volexx.com/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopiedReferral(true);
    setTimeout(() => setCopiedReferral(false), 2000);
  };

  const handleApplyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement promo code validation API call
    console.log('Applying promo code:', promoCode);
    setPromoCode('');
  };

  const shareReferral = (platform: string) => {
    const referralLink = `https://volexx.com/register?ref=${referralCode}`;
    const message = `Join Volexx and get $10 off your first delivery! Use my referral code: ${referralCode}`;
    
    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message + ' ' + referralLink)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(referralLink)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Promo & Referrals</h1>
          <p className="mt-2 text-gray-600">Save money and earn rewards</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Apply Promo Code */}
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
            <div className="flex items-center mb-4">
              <TicketIcon className="h-6 w-6 text-volexx-orange-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">Apply Promo Code</h2>
            </div>
            
            <form onSubmit={handleApplyPromo} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-orange-500 focus:border-volexx-orange-500 text-center text-lg font-semibold tracking-widest"
                />
              </div>
              <button type="submit" className="btn-primary w-full">
                Apply Code
              </button>
            </form>

            {appliedPromos.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Applied Promos</h3>
                {appliedPromos.map((promo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-volexx border border-green-200">
                    <div className="flex items-center">
                      <GiftIcon className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-semibold text-green-900">{promo.code}</p>
                        <p className="text-xs text-green-700">{promo.description}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-green-600">-${promo.discount}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 p-4 bg-volexx-blue-50 rounded-volexx border border-volexx-blue-200">
              <h3 className="text-sm font-semibold text-volexx-blue-900 mb-2">Current Promotions</h3>
              <ul className="text-sm text-volexx-blue-700 space-y-1">
                <li>• FIRST10 - $10 off your first order</li>
                <li>• BULK20 - 20% off bulk orders (10+)</li>
                <li>• WEEK15 - 15% off weekend deliveries</li>
              </ul>
            </div>
          </div>

          {/* Referral Program */}
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-volexx-blue-500 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">Referral Program</h2>
            </div>

            <div className="bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 rounded-volexx-lg p-6 text-white mb-6">
              <h3 className="text-lg font-semibold mb-2">Invite & Earn!</h3>
              <p className="text-sm opacity-90 mb-4">
                Refer friends and earn $10 for each successful referral. Your friend gets $10 off too!
              </p>
              <div className="bg-white bg-opacity-20 rounded-volexx p-3">
                <p className="text-xs opacity-75 mb-1">Your Referral Code</p>
                <div className="flex items-center justify-between">
                  <p className="text-xl font-bold tracking-wider">{referralCode}</p>
                  <button
                    onClick={copyReferralLink}
                    className="px-3 py-1 bg-white text-volexx-blue-600 rounded-volexx text-sm font-semibold hover:bg-opacity-90 transition-all"
                  >
                    {copiedReferral ? (
                      <>
                        <ClipboardDocumentCheckIcon className="h-4 w-4 inline mr-1" />
                        Copied!
                      </>
                    ) : (
                      'Copy Link'
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Referral Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-gray-50 rounded-volexx">
                <p className="text-2xl font-bold text-volexx-blue-600">{referrals.successful}</p>
                <p className="text-sm text-gray-600 mt-1">Successful Referrals</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-volexx">
                <p className="text-2xl font-bold text-green-600">${referrals.earnings.toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-1">Total Earnings</p>
              </div>
            </div>

            {/* Share Options */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Share via</h3>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => shareReferral('whatsapp')}
                  className="p-3 bg-green-500 text-white rounded-volexx hover:bg-green-600 transition-colors"
                >
                  <ShareIcon className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-xs font-semibold">WhatsApp</span>
                </button>
                <button
                  onClick={() => shareReferral('twitter')}
                  className="p-3 bg-blue-400 text-white rounded-volexx hover:bg-blue-500 transition-colors"
                >
                  <ShareIcon className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-xs font-semibold">Twitter</span>
                </button>
                <button
                  onClick={() => shareReferral('facebook')}
                  className="p-3 bg-blue-600 text-white rounded-volexx hover:bg-blue-700 transition-colors"
                >
                  <ShareIcon className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-xs font-semibold">Facebook</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-6 bg-white rounded-volexx-lg shadow-volexx-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">How the Referral Program Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-volexx-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-volexx-blue-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Share Your Code</h3>
              <p className="text-sm text-gray-600">Share your unique referral code with friends and family</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-volexx-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-volexx-blue-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">They Sign Up</h3>
              <p className="text-sm text-gray-600">Your friend creates an account using your referral code</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-volexx-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-volexx-blue-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Both Get Rewards</h3>
              <p className="text-sm text-gray-600">You both receive $10 credit after their first order</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoReferrals;
